<template>
  <div class="panel saved-heatmaps-panel">
    <div class="panel-header">
      <h3>Mis Vistas de Heatmap Guardadas</h3>
      <span v-if="savedHeatmaps.length > 0" class="count-badge">
        {{ activosCount }} activos de {{ savedHeatmaps.length }}
      </span>
    </div>

    <div v-if="loading" class="loading-state">Cargando...</div>

    <ul v-else-if="savedHeatmaps.length > 0" class="heatmaps-list">
      <li v-for="hm in savedHeatmaps" :key="hm.id" :class="{ 'is-inactive': !hm.is_active }">
        <div class="heatmap-info">
          <div class="heatmap-row-top">
            <button
              @click="toggleActive(hm)"
              :class="['status-toggle', hm.is_active ? 'active' : 'inactive']"
              :title="hm.is_active ? 'Click para desactivar' : 'Click para activar'"
            >
              <span class="status-dot"></span>
              {{ hm.is_active ? 'Activo' : 'Inactivo' }}
            </button>
            <span class="heatmap-name">{{ hm.name }}</span>
            <span v-if="hm.vista" :class="['vista-badge', hm.vista]" :title="hm.vista === 'web' ? 'Vista Web Propia' : 'Vista Marketplace'">
              <component :is="hm.vista === 'web' ? PhGlobe : PhShoppingBag" :size="11" weight="bold" /> {{ hm.vista === 'web' ? 'WEB' : 'MP' }}
            </span>
          </div>
          <div class="heatmap-meta">
            <span>{{ hm.product_count }} producto{{ hm.product_count !== 1 ? 's' : '' }}</span>
            <span v-if="hm.created_at">• {{ formatDate(hm.created_at) }}</span>
          </div>
        </div>
        <div class="actions">
          <button @click="$emit('edit-heatmap', hm)" class="action-btn edit-btn" title="Cargar productos para editar">
            <PhPencil :size="11" weight="bold" /> Editar
          </button>
          <a :href="`/heatmap/${hm.id}`" target="_blank" rel="noopener noreferrer" class="action-btn" title="Abrir en nueva pestaña">
            <PhImageSquare :size="11" weight="bold" /> Abrir
          </a>
          <button @click="deleteHeatmap(hm)" class="action-btn delete-btn" title="Eliminar">
            <PhTrash :size="11" weight="bold" />️
          </button>
        </div>
      </li>
    </ul>

    <div v-else class="empty-state">
      No tenés ninguna vista de heatmap guardada.
      <div class="empty-hint">Seleccioná productos con el checkbox y hacé click en "Guardar Vista" para crear uno.</div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import axios from 'axios';
import { PhGlobe, PhShoppingBag, PhPencil, PhImageSquare, PhTrash } from '@phosphor-icons/vue'

const API_BASE_URL = 'https://dash-visaovip.kompara.tech/api';
const savedHeatmaps = ref([]);
const loading = ref(true);

defineEmits(['edit-heatmap', 'heatmaps-changed']);

const activosCount = computed(() =>
  savedHeatmaps.value.filter(h => h.is_active).length
);

const getAuthHeaders = () => {
  const token = localStorage.getItem('token_precios');
  return { headers: { Authorization: `Bearer ${token}` } };
};

const fetchSavedHeatmaps = async () => {
  loading.value = true;
  try {
    const response = await axios.get(`${API_BASE_URL}/saved-heatmaps/`, getAuthHeaders());
    savedHeatmaps.value = response.data;
  } catch (error) {
    console.error('Error al cargar los heatmaps guardados:', error);
  } finally {
    loading.value = false;
  }
};

const toggleActive = async (hm) => {
  const prevState = hm.is_active;
  // Optimistic update
  hm.is_active = !hm.is_active;
  try {
    await axios.patch(
      `${API_BASE_URL}/saved-heatmaps/${hm.id}`,
      { is_active: hm.is_active },
      getAuthHeaders()
    );
  } catch (error) {
    console.error('Error al actualizar estado:', error);
    hm.is_active = prevState;  // rollback
    alert('No se pudo actualizar el estado del heatmap.');
  }
};

const deleteHeatmap = async (hm) => {
  if (!confirm(`¿Eliminar la vista "${hm.name}"?\n\nEsta acción no se puede deshacer.`)) return;
  try {
    await axios.delete(`${API_BASE_URL}/saved-heatmaps/${hm.id}`, getAuthHeaders());
    savedHeatmaps.value = savedHeatmaps.value.filter(h => h.id !== hm.id);
  } catch (error) {
    console.error('Error al eliminar:', error);
    alert('No se pudo eliminar el heatmap.');
  }
};

const formatDate = (isoString) => {
  if (!isoString) return '';
  const d = new Date(isoString);
  const now = new Date();
  const diffMs = now - d;
  const diffMin = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMin < 1) return 'hace unos segundos';
  if (diffMin < 60) return `hace ${diffMin} min`;
  if (diffHours < 24) return `hace ${diffHours} h`;
  if (diffDays < 30) return `hace ${diffDays} día${diffDays !== 1 ? 's' : ''}`;
  return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
};

// Exponemos el fetch para que el padre pueda refrescar la lista cuando se crea uno nuevo
defineExpose({ fetchSavedHeatmaps });

onMounted(fetchSavedHeatmaps);
</script>

<style scoped>
.saved-heatmaps-panel { /* hereda .panel */ }

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.panel-header h3 {
  margin: 0;
}
.count-badge {
  font-family: var(--font-mono);
  font-size: 10.5px;
  background: var(--color-surface-2);
  border: 1px solid var(--color-border-strong);
  color: var(--color-text-muted);
  border-radius: 3px;
  padding: 2px 8px;
  font-weight: 600;
  letter-spacing: 0.05em;
}

.loading-state {
  padding: 20px;
  text-align: center;
  color: var(--color-text-muted);
  font-family: var(--font-mono);
  font-size: 11px;
  font-style: italic;
}

.heatmaps-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.heatmaps-list li {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 9px 12px;
  border: 1px solid var(--color-border);
  border-left: 3px solid var(--color-accent);
  border-radius: 2px;
  background: var(--color-surface-2);
  transition: all 0.15s;
}
.heatmaps-list li:hover {
  background: var(--color-hover-row);
  border-color: var(--color-border-strong);
  border-left-color: var(--color-accent);
}
.heatmaps-list li.is-inactive {
  opacity: 0.55;
  border-left-color: var(--color-text-faint);
}

.heatmap-info {
  flex: 1;
  min-width: 0;
}
.heatmap-row-top {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}
.heatmap-name {
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 12.5px;
  color: var(--color-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.heatmap-meta {
  font-family: var(--font-mono);
  font-size: 10px;
  color: var(--color-text-muted);
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  letter-spacing: 0.03em;
}

/* Toggle estado activo/inactivo */
.status-toggle {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 7px;
  border-radius: 2px;
  font-family: var(--font-mono);
  font-size: 9.5px;
  font-weight: 700;
  cursor: pointer;
  border: 1px solid;
  transition: all 0.15s;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}
.status-toggle.active {
  background: var(--color-success-bg);
  border-color: var(--color-success);
  color: var(--color-success-text);
}
.status-toggle.inactive {
  background: var(--color-surface);
  border-color: var(--color-border-strong);
  color: var(--color-text-muted);
}
.status-toggle:hover {
  filter: brightness(1.15);
}
.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}

/* Acciones */
.actions {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}
.action-btn {
  background: transparent;
  border: 1px solid var(--color-border-strong);
  color: var(--color-text-muted);
  padding: 4px 9px;
  border-radius: 2px;
  cursor: pointer;
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 600;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  letter-spacing: 0.03em;
  transition: all 0.15s;
}
.action-btn:hover {
  color: var(--color-text);
  border-color: var(--color-text-muted);
}
.edit-btn:hover {
  color: var(--color-accent);
  border-color: var(--color-accent-border);
}
.delete-btn:hover {
  color: var(--color-danger);
  border-color: var(--color-danger);
}

/* Estado vacío */
.empty-state {
  padding: 16px;
  text-align: center;
  color: var(--color-text-muted);
  font-size: 12px;
  font-style: italic;
  background: var(--color-surface-2);
  border: 1px dashed var(--color-border-strong);
  border-radius: 2px;
}
.empty-hint {
  margin-top: 6px;
  font-size: 11px;
  color: var(--color-text-faint);
}

/* Vista badge */
.vista-badge {
  font-family: var(--font-mono);
  font-size: 9px;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 2px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  flex-shrink: 0;
  border: 1px solid;
}
.vista-badge.web {
  background: rgba(56, 189, 248, 0.10);
  color: var(--color-min-price-text);
  border-color: rgba(56, 189, 248, 0.35);
}
.vista-badge.marketplace {
  background: var(--color-leader-bg);
  color: var(--color-leader-text);
  border-color: var(--color-leader);
}
</style>